import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Multiselect from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import api from '../services/apiService';
import { getUser } from '../utils/auth';

const selectionKey = (machineId) => String(machineId);

export default function InterestsPage() {
  const user = getUser();
  const canEdit = user?.role === 'tenant_admin';
  const [categories, setCategories] = useState([]);
  const [stations, setStations] = useState([]);
  const [selected, setSelected] = useState(new Map());
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStations, setSelectedStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.get('/tenant/interests')
      .then((response) => {
        const loadedCategories = response.data.categories || response.data;
        const loadedStations = response.data.stations || [];
        const initialSelection = new Map();
        loadedCategories.forEach((category) => {
          category.types.forEach((type) => {
            if (!type.selected) return;
            initialSelection.set(selectionKey(type.machineId), {
              machineId: type.machineId,
              categoryId: category.id,
              familyName: category.familyName || 'Weitere Maschinen',
              categoryName: category.name,
              manufacturer: type.manufacturer,
              manufacturerType: type.manufacturerType,
              liftHeight: type.liftHeight,
              workingHeight: type.workingHeight,
              drive: type.drive,
              weight: type.weight,
              stationIds: type.selectedStationIds || [],
              stationNames: (type.selectedStationIds || []).map((stationId) => (
                loadedStations.find((station) => String(station.id) === String(stationId))?.name
              )).filter(Boolean),
            });
          });
        });
        setCategories(loadedCategories);
        setStations(loadedStations);
        setSelected(initialSelection);
      })
      .catch(() => setNotice({ type: 'error', text: 'Das Maschinenangebot konnte nicht geladen werden.' }))
      .finally(() => setLoading(false));
  }, []);

  const familyOptions = useMemo(() => Array.from(new Set(
    categories.map((category) => category.familyName || 'Weitere Maschinen')
  )).sort((a, b) => a.localeCompare(b, 'de')).map((family) => ({
    label: family,
    value: family,
  })), [categories]);

  const categoryOptions = useMemo(() => categories
    .filter((category) => (category.familyName || 'Weitere Maschinen') === selectedFamily?.value)
    .map((category) => ({
      label: category.name,
      value: String(category.id),
    })), [categories, selectedFamily]);

  const activeCategory = useMemo(() => categories.find(
    (category) => String(category.id) === selectedCategory?.value
  ), [categories, selectedCategory]);

  const typeOptions = useMemo(() => (activeCategory?.types || []).map((type) => ({
    label: type.manufacturerType,
    value: String(type.machineId),
    description: type.manufacturer,
  })), [activeCategory]);

  const stationOptions = useMemo(() => stations.filter((station) => station.isActive).map((station) => ({
    label: station.name,
    value: String(station.id),
    description: `${station.postalCode} ${station.city} · ${station.deliveryRadiusKm} km Radius`,
  })), [stations]);

  const selectedRows = useMemo(() => Array.from(selected.values()).sort((a, b) => (
    a.familyName.localeCompare(b.familyName, 'de')
      || a.categoryName.localeCompare(b.categoryName, 'de')
      || a.manufacturer.localeCompare(b.manufacturer, 'de')
      || a.manufacturerType.localeCompare(b.manufacturerType, 'de')
  )), [selected]);

  function changeFamily(option) {
    setSelectedFamily(option);
    setSelectedCategory(null);
    setSelectedType(null);
    setSelectedStations([]);
  }

  function changeCategory(option) {
    setSelectedCategory(option);
    setSelectedType(null);
    setSelectedStations([]);
  }

  function addSelection() {
    if (!activeCategory || !selectedType || selectedStations.length === 0) return;
    const type = activeCategory.types.find((entry) => String(entry.machineId) === selectedType.value);
    if (!type) return;
    const key = selectionKey(type.machineId);
    setSelected((current) => {
      const next = new Map(current);
      next.set(key, {
        machineId: type.machineId,
        categoryId: activeCategory.id,
        familyName: activeCategory.familyName || 'Weitere Maschinen',
        categoryName: activeCategory.name,
        manufacturer: type.manufacturer,
        manufacturerType: type.manufacturerType,
        liftHeight: type.liftHeight,
        workingHeight: type.workingHeight,
        drive: type.drive,
        weight: type.weight,
        stationIds: selectedStations.map((station) => station.value),
        stationNames: selectedStations.map((station) => station.label),
      });
      return next;
    });
    setSelectedType(null);
    setSelectedStations([]);
    setNotice(null);
  }

  function removeSelection(item) {
    setSelected((current) => {
      const next = new Map(current);
      next.delete(selectionKey(item.machineId));
      return next;
    });
    setNotice(null);
  }

  async function save() {
    setSaving(true);
    setNotice(null);
    try {
      await api.put('/tenant/interests', {
        selections: selectedRows.map(({ machineId, stationIds }) => ({ machineId, stationIds })),
      });
      setNotice({ type: 'success', text: 'Ihr Maschinenangebot wurde gespeichert.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.error || 'Das Maschinenangebot konnte nicht gespeichert werden.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Legen Sie genau fest, für welche Familie, Produktgruppe und welchen Herstellertyp Sie Mietanfragen erhalten möchten."
        actions={canEdit && (
          <Button variant="primary" loading={saving} onClick={save}>
            Maschinenangebot speichern
          </Button>
        )}
      >
        Maschinenangebot
      </Header>

      {notice && <Alert type={notice.type}>{notice.text}</Alert>}
      {!canEdit && (
        <Alert type="info">
          Nur Vermieter-Administratoren können das Maschinenangebot ändern.
        </Alert>
      )}

      {loading ? <Spinner size="large" /> : (
        <>
          {canEdit && categories.length > 0 && (
            <Container header={<Header variant="h2">Maschinenart hinzufügen</Header>}>
              <SpaceBetween size="l">
                <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
                  <FormField label="1. Familie">
                    <Select
                      selectedOption={selectedFamily}
                      onChange={({ detail }) => changeFamily(detail.selectedOption)}
                      options={familyOptions}
                      placeholder="Familie wählen"
                      empty="Keine Familie vorhanden"
                    />
                  </FormField>
                  <FormField label="2. Produktgruppe">
                    <Select
                      selectedOption={selectedCategory}
                      onChange={({ detail }) => changeCategory(detail.selectedOption)}
                      options={categoryOptions}
                      placeholder="Produktgruppe wählen"
                      empty="Keine Produktgruppe vorhanden"
                      disabled={!selectedFamily}
                    />
                  </FormField>
                  <FormField label="3. Herstellertyp">
                    <Select
                      selectedOption={selectedType}
                      onChange={({ detail }) => {
                        setSelectedType(detail.selectedOption);
                        setSelectedStations([]);
                      }}
                      options={typeOptions}
                      placeholder="Herstellertyp wählen"
                      empty="Kein Herstellertyp vorhanden"
                      disabled={!selectedCategory}
                    />
                  </FormField>
                  <FormField label="4. Mietstation" description="Mehrfachauswahl möglich">
                    <Multiselect
                      selectedOptions={selectedStations}
                      onChange={({ detail }) => setSelectedStations(detail.selectedOptions)}
                      options={stationOptions}
                      placeholder="Mietstation wählen"
                      empty="Keine aktive Mietstation vorhanden"
                      disabled={!selectedType}
                    />
                  </FormField>
                </Grid>
                <Button
                  variant="normal"
                  iconName="add-plus"
                  disabled={!selectedType || selectedStations.length === 0}
                  onClick={addSelection}
                >
                  Zum Maschinenangebot hinzufügen
                </Button>
              </SpaceBetween>
            </Container>
          )}

          <Table
            header={(
              <Header
                variant="h2"
                counter={`(${selectedRows.length})`}
                description="Diese Kombinationen werden für die Verteilung passender Mietanfragen verwendet."
              >
                Ihr Maschinenangebot
              </Header>
            )}
            items={selectedRows}
            columnDefinitions={[
              { id: 'family', header: 'Familie', cell: (item) => item.familyName },
              { id: 'category', header: 'Produktgruppe', cell: (item) => item.categoryName },
              { id: 'manufacturer', header: 'Hersteller', cell: (item) => item.manufacturer },
              { id: 'type', header: 'Herstellertyp', cell: (item) => item.manufacturerType },
              { id: 'liftHeight', header: 'Hubhöhe', cell: (item) => item.liftHeight },
              { id: 'workingHeight', header: 'Arbeitshöhe', cell: (item) => item.workingHeight },
              { id: 'drive', header: 'Antrieb', cell: (item) => item.drive },
              { id: 'weight', header: 'Gewicht', cell: (item) => item.weight },
              {
                id: 'stations',
                header: 'Mietstationen',
                cell: (item) => item.stationNames.length ? item.stationNames.join(', ') : 'Keine Station zugeordnet',
              },
              ...(canEdit ? [{
                id: 'actions',
                header: 'Aktion',
                cell: (item) => (
                  <Button
                    variant="inline-link"
                    iconName="remove"
                    onClick={() => removeSelection(item)}
                  >
                    Entfernen
                  </Button>
                ),
              }] : []),
            ]}
            empty={(
              <Alert type="info">
                Es wurde noch kein Maschinenangebot hinterlegt. Wählen Sie Familie, Produktgruppe und Herstellertyp aus.
              </Alert>
            )}
            variant="container"
          />

          {categories.length === 0 && (
            <Alert>Es wurden noch keine Maschinen aus Datasheet importiert.</Alert>
          )}
          {canEdit && stations.filter((station) => station.isActive).length === 0 && (
            <Alert type="warning">
              Es ist noch keine aktive Mietstation vorhanden. Der Plattformadministrator muss zuerst eine Mietstation mit Radius anlegen.
            </Alert>
          )}
        </>
      )}
    </SpaceBetween>
  );
}
